/**
 * Reading a simulation result.
 *
 * Pure functions, no React and no store — everything a component would
 * otherwise be tempted to compute inline while rendering.
 */

import type { SimBaySnapshot, SimCommand, SimProduct, SimRun } from '../types'

/**
 * A difference below this is noise, and saying so is more useful than a number.
 *
 * Two runs of the same yard differ slightly for reasons nobody can act on —
 * a truck reaching a bay a minute earlier changes who gets called next. Only
 * differences past these thresholds are worth putting in front of a manager.
 */
const MATERIAL_P90_MIN = 5
const MATERIAL_SERVED = 2

export interface RunComparisonRow {
  key: string
  name: string
  baselineName: string
  deltaP90: number
  deltaWorst: number
  deltaServed: number
  deltaIdle: number
  deltaOvertakes: number
}

/** Every run after the first, measured against the first. */
export function compareRuns(runs: SimRun[]): RunComparisonRow[] {
  const [baseline, ...rest] = runs
  if (!baseline) return []

  return rest.map((run) => ({
    key: run.key,
    name: run.name,
    baselineName: baseline.name,
    deltaP90: run.metrics.waitToCall.p90 - baseline.metrics.waitToCall.p90,
    deltaWorst: run.metrics.waitToCall.max - baseline.metrics.waitToCall.max,
    deltaServed: run.metrics.served - baseline.metrics.served,
    deltaIdle: run.metrics.idleWithWaitingSlotMin - baseline.metrics.idleWithWaitingSlotMin,
    deltaOvertakes: run.metrics.overtakes - baseline.metrics.overtakes,
  }))
}

export function isMaterialDifference(row: RunComparisonRow): boolean {
  return Math.abs(row.deltaP90) >= MATERIAL_P90_MIN || Math.abs(row.deltaServed) >= MATERIAL_SERVED
}

/** Minutes since the yard was read, as a clock the operator recognises. */
export function simClock(liveAt: string | undefined, min: number): string {
  const base = liveAt ? new Date(liveAt).getTime() : Date.now()
  const at = new Date(base + min * 60_000)
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
}

/** How many trucks a bay is holding in any form. */
export function bayLoad(bay: SimBaySnapshot): number {
  return bay.inside.length + bay.held.length
}

/** Products this bay will take, primary first. */
export function bayProductIds(bay: SimBaySnapshot): number[] {
  return [bay.variantId, ...bay.allowedVariantIds].filter((id) => id > 0)
}

/**
 * Whether this bay can load this product.
 *
 * The same rule the server applies, so the picker cannot offer a bay that
 * assignment would refuse.
 */
export function bayCanLoad(bay: SimBaySnapshot, variantId: number): boolean {
  return bay.variantId === variantId || bay.allowedVariantIds.includes(variantId)
}

/** A truck built from a real order, ready to send into the simulation. */
export function commandFromOrder(
  order: { variantId: number; variantCode: string; packaging: 'bulk' | 'bags'; orderNumber: string },
  products: SimProduct[],
  atMin: number,
  options?: { noShow?: boolean; smsFails?: boolean },
): SimCommand {
  const product = products.find((p) => p.variantId === order.variantId)
  return {
    atMin,
    kind: 'addTruck',
    variantId: order.variantId,
    variantCode: order.variantCode,
    packaging: order.packaging,
    // The measured time for this product, which is the honest default. A person
    // can override it, but guessing high or low by hand is how a simulation
    // quietly becomes a story.
    serviceMin: product?.medianLoadMin ?? 45,
    responseMin: 10,
    noShow: options?.noShow,
    smsFails: options?.smsFails,
    orderNumber: order.orderNumber,
  }
}

/** Plain-language summary of what a command did, for the log of changes. */
export function describeCommand(
  command: SimCommand,
  bays: SimBaySnapshot[],
  products: SimProduct[],
): string {
  const bayLabel = (id: number) => bays.find((b) => b.id === id)?.label ?? `bay ${id}`
  const productCode = (id: number) =>
    products.find((p) => p.variantId === id)?.variantCode ?? String(id)

  switch (command.kind) {
    case 'addTruck':
      return command.orderNumber
        ? `${command.orderNumber} sent in (${command.variantCode})`
        : `truck sent in (${command.variantCode})`
    case 'closeBay':
      return `${bayLabel(command.bayId)} closed`
    case 'openBay':
      return `${bayLabel(command.bayId)} reopened`
    case 'setBayProduct':
      return `${bayLabel(command.bayId)} set to ${productCode(command.variantId)}`
    case 'setConfig':
      return command.config.scope === 'pool' ? 'switched to pooled' : 'switched to per-bay'
    default:
      return 'changed'
  }
}
